<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative py-20 bg-gradient-to-r from-[#1a2a4d] to-[#233862]">
      <div class="container mx-auto px-4">
        <div class="text-center">
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-4">
            {{ currentLanguage === 'el' ? 'Ιστολόγιο Ακτοπλοϊκών Ταξιδιών' : 'Ferry Travel Blog' }}
          </h1>
          <p class="text-xl text-white/80 max-w-2xl mx-auto">
            {{ currentLanguage === 'el' 
              ? 'Διαβάστε τις τελευταίες ενημερώσεις, ταξιδιωτικούς οδηγούς και συμβουλές για τα ακτοπλοϊκά ταξίδια στα ελληνικά νησιά.'
              : 'Discover travel tips, guides, and updates about Greek island ferries' }}
          </p>
        </div>
      </div>
    </section>

    <!-- Blog Posts Grid -->
    <section class="py-16">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <router-link
            v-for="post in blogPosts"
            :key="post.id"
            :to="{ name: getRouteName('blog-post'), params: { id: post.id }}"
            class="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
          >
            <div class="relative h-48 overflow-hidden">
              <img
                :src="post.image"
                :alt="post.title"
                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div class="p-6">
              <h2 class="text-xl font-bold text-[#233862] mb-2">{{ post.title }}</h2>
              <p class="text-gray-600 text-sm mb-4">{{ post.excerpt }}</p>
              <div class="flex items-center justify-between text-sm text-gray-500">
                <span>{{ formatDate(post.date) }}</span>
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import blogDataEn from '@/data/blog.json'
import blogDataEl from '@/data/blog.el.json'

const route = useRoute()

// Compute current language from route
const currentLanguage = computed(() => {
  return route.path.includes('/el/') ? 'el' : 'en'
})

// Compute route names with language prefix
const getRouteName = (baseName) => {
  return currentLanguage.value === 'el' ? `el${baseName}` : baseName
}

// Import all images
const images = import.meta.glob('@/assets/images/**/*.webp', { eager: true })

// Function to get image URL
const getImageUrl = (imagePath) => {
  try {
    // Remove the @ symbol from the path
    const cleanPath = imagePath.replace('@', '')
    const key = Object.keys(images).find(key => key.includes(cleanPath.split('/').pop()))
    return key ? images[key].default : ''
  } catch (error) {
    console.error('Error loading image:', error)
    return ''
  }
}

const blogPosts = computed(() => {
  const posts = currentLanguage.value === 'el' ? blogDataEl.posts : blogDataEn.posts
  return posts.map(post => ({
    ...post,
    image: getImageUrl(post.image)
  }))
})

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(dateString).toLocaleDateString(
    currentLanguage.value === 'el' ? 'el-GR' : 'en-US',
    options
  )
}

onMounted(() => {
  const title = currentLanguage.value === 'el'
    ? 'Ιστολόγιο Ακτοπλοϊκών Ταξιδιών - Τελευταίες Ενημερώσεις & Οδηγοί | Aktoploika'
    : 'Ferry Travel Blog - Latest Updates & Guides | Aktoploika'
  
  const description = currentLanguage.value === 'el'
    ? 'Διαβάστε τις τελευταίες ενημερώσεις, ταξιδιωτικούς οδηγούς και συμβουλές για τα ακτοπλοϊκά ταξίδια στα ελληνικά νησιά.'
    : 'Read the latest updates, travel guides, and tips about ferry travel in the Greek islands.'

  document.title = title
  const metaDesc = document.querySelector('meta[name="description"]')
  if (metaDesc) {
    metaDesc.setAttribute('content', description)
  }
})
</script>
